import React from "react";
import styled from "@emotion/styled";
import {
  fontFamily,
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  border as borderToken,
  interactive,
  surface,
  text,
} from "@atlas-int/tokens";
import CodeBlock from "../../atlas-ui/components/CodeBlock";

// ── Styled primitives ────────────────────────────────────────────

const Section = styled.section`
  margin-bottom: ${spacing[16]};
`;

const PageTitle = styled.h1`
  font-size: ${fontSize["3xl"]};
  font-weight: ${fontWeight.bold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const PageSubtitle = styled.p`
  font-size: ${fontSize.lg};
  color: ${text.secondary};
  margin: 0 0 ${spacing[12]} 0;
  line-height: 1.5;
`;

const SectionTitle = styled.h2`
  font-size: ${fontSize["2xl"]};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[6]} 0;
`;

const SubSectionTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: ${spacing[8]} 0 ${spacing[4]} 0;
`;

const Paragraph = styled.p`
  font-size: ${fontSize.base};
  color: ${text.secondary};
  line-height: 1.7;
  margin: 0 0 ${spacing[4]} 0;
  max-width: 72ch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${fontSize.sm};
  margin-bottom: ${spacing[6]};
`;

const Th = styled.th`
  text-align: left;
  padding: ${spacing[3]} ${spacing[4]};
  font-weight: ${fontWeight.semibold};
  color: ${text.secondary};
  border-bottom: 2px solid ${borderToken.default};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${spacing[3]} ${spacing[4]};
  color: ${text.primary};
  border-bottom: 1px solid ${borderToken.subtle};
  vertical-align: top;
  line-height: 1.5;
`;

const Mono = styled.code`
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.sm};
  color: ${interactive.default};
  font-weight: ${fontWeight.semibold};
`;

const Badge = styled.span<{ $variant?: "url" | "apollo" | "context" | "local" }>`
  display: inline-block;
  padding: ${spacing[1]} ${spacing[3]};
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.xs};
  border-radius: ${borderRadius.base};
  margin-right: ${spacing[2]};
  background-color: ${({ $variant }) => {
    switch ($variant) {
      case "url":
        return interactive.default;
      case "apollo":
        return "#6C5CE7";
      case "context":
        return "#00B894";
      case "local":
        return text.secondary;
      default:
        return surface.muted;
    }
  }};
  color: ${text.onDark};
`;

const DiagramContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${spacing[6]} 0;
  margin-bottom: ${spacing[6]};
`;

const CalloutBox = styled.div`
  background-color: ${surface.muted};
  border-left: 4px solid ${interactive.default};
  border-radius: ${borderRadius.base};
  padding: ${spacing[6]} ${spacing[8]};
  margin: ${spacing[6]} 0;
`;

const CalloutTitle = styled.div`
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin-bottom: ${spacing[2]};
`;

const BulletList = styled.ul`
  margin: ${spacing[2]} 0 ${spacing[4]} 0;
  padding-left: ${spacing[8]};
  color: ${text.secondary};
  line-height: 1.7;
`;

// ── Data flow SVG diagram ────────────────────────────────────────

const DataFlowDiagram: React.FC = () => {
  const boxW = 160;
  const boxH = 36;
  const svgW = 720;
  const svgH = 420;

  const boxes: Array<{
    id: string;
    label: string;
    x: number;
    y: number;
    color: string;
  }> = [
    { id: "url", label: "URL Search Params", x: 280, y: 10, color: interactive.default },
    { id: "hooks", label: "useExploreParams()", x: 280, y: 80, color: interactive.default },
    { id: "controls", label: "Controls", x: 60, y: 170, color: "#00B894" },
    { id: "metadata", label: "Metadata Hooks", x: 280, y: 170, color: "#6C5CE7" },
    { id: "apollo", label: "Apollo Cache", x: 500, y: 170, color: "#6C5CE7" },
    { id: "query", label: "useQuery()", x: 500, y: 260, color: "#6C5CE7" },
    { id: "transform", label: "Data Transform", x: 280, y: 260, color: text.secondary },
    { id: "chart", label: "Chart Renderer", x: 280, y: 340, color: text.primary },
    { id: "tooltip", label: "Tooltip (local)", x: 500, y: 340, color: text.secondary },
  ];

  const arrows: Array<{ from: string; to: string }> = [
    { from: "url", to: "hooks" },
    { from: "hooks", to: "controls" },
    { from: "hooks", to: "metadata" },
    { from: "hooks", to: "apollo" },
    { from: "metadata", to: "apollo" },
    { from: "apollo", to: "query" },
    { from: "query", to: "transform" },
    { from: "metadata", to: "transform" },
    { from: "transform", to: "chart" },
    { from: "chart", to: "tooltip" },
  ];

  const getBox = (id: string) => boxes.find((b) => b.id === id)!;

  return (
    <DiagramContainer>
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
        <defs>
          <marker
            id="flow-arrow"
            viewBox="0 0 10 6"
            refX="10"
            refY="3"
            markerWidth="8"
            markerHeight="6"
            orient="auto"
          >
            <path d="M 0 0 L 10 3 L 0 6 Z" fill={borderToken.default} />
          </marker>
        </defs>

        {/* Arrows */}
        {arrows.map(({ from, to }) => {
          const f = getBox(from);
          const t = getBox(to);
          const x1 = f.x + boxW / 2;
          const y1 = f.y + boxH;
          const x2 = t.x + boxW / 2;
          const y2 = t.y;
          return (
            <line
              key={`${from}-${to}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={borderToken.default}
              strokeWidth={1.5}
              markerEnd="url(#flow-arrow)"
            />
          );
        })}

        {/* Controls → URL feedback arrow */}
        <path
          d={`M ${60 + boxW / 2} ${170} Q ${60 + boxW / 2} ${45} ${280} ${28 + boxH / 2}`}
          fill="none"
          stroke={interactive.default}
          strokeWidth={1.5}
          strokeDasharray="6 3"
          markerEnd="url(#flow-arrow)"
        />
        <text
          x={130}
          y={85}
          fill={text.muted}
          fontSize="10"
          fontFamily={fontFamily.mono}
        >
          setQuery()
        </text>

        {/* Boxes */}
        {boxes.map((box) => (
          <g key={box.id}>
            <rect
              x={box.x}
              y={box.y}
              width={boxW}
              height={boxH}
              rx={6}
              ry={6}
              fill={surface.raised}
              stroke={box.color}
              strokeWidth={2}
            />
            <text
              x={box.x + boxW / 2}
              y={box.y + boxH / 2 + 1}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={text.primary}
              fontFamily={fontFamily.mono}
              fontSize="11"
            >
              {box.label}
            </text>
          </g>
        ))}
      </svg>
    </DiagramContainer>
  );
};

// ── Component tree diagram ───────────────────────────────────────

const ComponentTreeDiagram: React.FC = () => {
  const svgW = 700;
  const svgH = 320;
  const boxW = 150;
  const boxH = 30;

  const boxes: Array<{
    id: string;
    label: string;
    x: number;
    y: number;
    color: string;
  }> = [
    { id: "visapp", label: "VisApp", x: 275, y: 10, color: text.primary },
    { id: "controls", label: "ControlsPanel", x: 60, y: 80, color: "#00B894" },
    { id: "layout", label: "VizLayout", x: 275, y: 80, color: text.secondary },
    { id: "outlet", label: "Connected Chart", x: 490, y: 80, color: "#6C5CE7" },
    { id: "loc", label: "LocationDropdown", x: 0, y: 160, color: "#00B894" },
    { id: "prod", label: "ProductDropdown", x: 155, y: 160, color: "#00B894" },
    { id: "year", label: "YearInputs", x: 310, y: 160, color: "#00B894" },
    { id: "settings", label: "Settings", x: 465, y: 160, color: "#00B894" },
    { id: "title", label: "TitleGenerator", x: 155, y: 240, color: text.secondary },
    { id: "find", label: "FindInViz", x: 310, y: 240, color: text.secondary },
    { id: "chart", label: "Chart + Tooltip", x: 465, y: 240, color: "#6C5CE7" },
  ];

  const edges: Array<{ from: string; to: string }> = [
    { from: "visapp", to: "controls" },
    { from: "visapp", to: "layout" },
    { from: "visapp", to: "outlet" },
    { from: "controls", to: "loc" },
    { from: "controls", to: "prod" },
    { from: "controls", to: "year" },
    { from: "controls", to: "settings" },
    { from: "layout", to: "title" },
    { from: "layout", to: "find" },
    { from: "outlet", to: "chart" },
  ];

  const getBox = (id: string) => boxes.find((b) => b.id === id)!;

  return (
    <DiagramContainer>
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
        <defs>
          <marker
            id="tree-arrow"
            viewBox="0 0 10 6"
            refX="10"
            refY="3"
            markerWidth="7"
            markerHeight="5"
            orient="auto"
          >
            <path d="M 0 0 L 10 3 L 0 6 Z" fill={borderToken.default} />
          </marker>
        </defs>

        {edges.map(({ from, to }) => {
          const f = getBox(from);
          const t = getBox(to);
          return (
            <line
              key={`${from}-${to}`}
              x1={f.x + boxW / 2}
              y1={f.y + boxH}
              x2={t.x + boxW / 2}
              y2={t.y}
              stroke={borderToken.default}
              strokeWidth={1.5}
              markerEnd="url(#tree-arrow)"
            />
          );
        })}

        {boxes.map((box) => (
          <g key={box.id}>
            <rect
              x={box.x}
              y={box.y}
              width={boxW}
              height={boxH}
              rx={5}
              ry={5}
              fill={surface.raised}
              stroke={box.color}
              strokeWidth={1.5}
            />
            <text
              x={box.x + boxW / 2}
              y={box.y + boxH / 2 + 1}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={text.primary}
              fontFamily={fontFamily.mono}
              fontSize="11"
            >
              {box.label}
            </text>
          </g>
        ))}
      </svg>
    </DiagramContainer>
  );
};

// ── Main page ────────────────────────────────────────────────────

const ArchitecturePage: React.FC = () => {
  return (
    <div>
      <PageTitle>Atlas Site Architecture</PageTitle>
      <PageSubtitle>
        How the atlas website integrates the five packages, manages browser
        state, and renders data visualizations.
      </PageSubtitle>

      {/* ── Route Structure ─────────────────────────────────── */}
      <Section>
        <SectionTitle>Route Structure</SectionTitle>
        <Paragraph>
          The app uses React Router v7 with layout routes. Each major section
          has its own layout component that wraps an <Mono>{"<Outlet />"}</Mono>.
        </Paragraph>
        <Table>
          <thead>
            <tr>
              <Th>Path</Th>
              <Th>Layout</Th>
              <Th>Description</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td><Mono>/</Mono></Td>
              <Td>Landing</Td>
              <Td>Home page, standalone layout</Td>
            </tr>
            <tr>
              <Td><Mono>/explore/*</Mono></Td>
              <Td>VisApp</Td>
              <Td>
                Visualization explore tool. Child routes: <Mono>/treemap</Mono>,{" "}
                <Mono>/geomap</Mono>, <Mono>/overtime</Mono>,{" "}
                <Mono>/marketshare</Mono>, <Mono>/productspace</Mono>,{" "}
                <Mono>/feasibility</Mono>
              </Td>
            </tr>
            <tr>
              <Td><Mono>/countries/*</Mono></Td>
              <Td>CountryProfilesWrapper</Td>
              <Td>Country profiles (lazy-loaded, uses Redux internally)</Td>
            </tr>
            <tr>
              <Td><Mono>/rankings/*</Mono></Td>
              <Td>RankingsLayout</Td>
              <Td>Country & product rankings (lazy-loaded)</Td>
            </tr>
            <tr>
              <Td><Mono>/glossary</Mono>, <Mono>/faq</Mono>, <Mono>/about</Mono>, etc.</Td>
              <Td>StaticPage</Td>
              <Td>Content pages with shared header/footer</Td>
            </tr>
            <tr>
              <Td><Mono>/trade-data-methodology</Mono></Td>
              <Td>FullWidthPage</Td>
              <Td>Standalone full-width layout</Td>
            </tr>
            <tr>
              <Td><Mono>/packages/*</Mono></Td>
              <Td>PackagesLayout</Td>
              <Td>This documentation (dev only)</Td>
            </tr>
          </tbody>
        </Table>
      </Section>

      {/* ── State Management ────────────────────────────────── */}
      <Section>
        <SectionTitle>State Management</SectionTitle>
        <Paragraph>
          The app uses <strong>no external state libraries</strong> (no Redux,
          Zustand, or Jotai) for the explore tool. State is managed through
          four layers, each with a clear responsibility.
        </Paragraph>

        <Table>
          <thead>
            <tr>
              <Th>Layer</Th>
              <Th>Mechanism</Th>
              <Th>What It Stores</Th>
              <Th>Scope</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>
                <Badge $variant="url">URL</Badge>
              </Td>
              <Td>Search params via React Router</Td>
              <Td>
                All visualization selections: exporter, importer, year, product
                class, product, view, trade direction, colorBy, tradeFlow,
                RCA threshold, etc.
              </Td>
              <Td>Shareable, bookmarkable, back/forward compatible</Td>
            </tr>
            <tr>
              <Td>
                <Badge $variant="apollo">Apollo</Badge>
              </Td>
              <Td>Apollo InMemoryCache</Td>
              <Td>
                All GraphQL query results: metadata (locations, products, year
                ranges), trade data, availability matrices
              </Td>
              <Td>Session-wide, automatic deduplication & caching</Td>
            </tr>
            <tr>
              <Td>
                <Badge $variant="context">Context</Badge>
              </Td>
              <Td>React Context (2 providers)</Td>
              <Td>
                Theme (OS preference), Tutorial state (Product Space
                onboarding, backed by localStorage)
              </Td>
              <Td>App-wide, UI-only</Td>
            </tr>
            <tr>
              <Td>
                <Badge $variant="local">Local</Badge>
              </Td>
              <Td>useState</Td>
              <Td>
                Tooltip hover target, hidden legend categories, search filter
                text, mobile breakpoint detection
              </Td>
              <Td>Per-component, ephemeral, never persisted</Td>
            </tr>
          </tbody>
        </Table>

        <CalloutBox>
          <CalloutTitle>URL is the single source of truth</CalloutTitle>
          <Paragraph>
            Every visualization parameter is serialized into the URL via Zod
            schemas from <Mono>@atlas-int/data</Mono>. Default values are
            omitted from the URL to keep it clean. Refreshing, sharing, or
            using browser back/forward restores the exact state.
          </Paragraph>
        </CalloutBox>

        <SubSectionTitle>URL Param Hooks</SubSectionTitle>
        <Paragraph>
          The <Mono>src/visualization/paramSchemas.ts</Mono> file provides
          typed hooks that wrap React Router&apos;s <Mono>useSearchParams</Mono>{" "}
          with Zod validation from <Mono>@atlas-int/data</Mono>.
        </Paragraph>
        <CodeBlock
          code={`// Read params for the current viz type (type-narrowed)
const [params, setQuery] = useTreemapParams();

// Read cross-viz params (union type)
const [params, setQuery] = useExploreParams();

// Update URL (merges with current, omits defaults)
setQuery({ exporter: "USA", year: 2020 });

// Zod schema validates & coerces every read:
// parseSearchParams(vizType, searchParams) → typed params`}
        />

        <SubSectionTitle>Per-Viz Schemas</SubSectionTitle>
        <Table>
          <thead>
            <tr>
              <Th>Hook</Th>
              <Th>Schema Source</Th>
              <Th>Viz-specific Params</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td><Mono>useTreemapParams()</Mono></Td>
              <Td><Mono>treemapParamSchema</Mono></Td>
              <Td>colorBy, locationLevel, tradeFlow</Td>
            </tr>
            <tr>
              <Td><Mono>useGeomapParams()</Mono></Td>
              <Td><Mono>geoParamSchema</Mono></Td>
              <Td>geoMapVariable</Td>
            </tr>
            <tr>
              <Td><Mono>useOvertimeParams()</Mono></Td>
              <Td><Mono>overtimeParamSchema</Mono></Td>
              <Td>yAxis, layout, ordering, locationLevel, tradeFlow</Td>
            </tr>
            <tr>
              <Td><Mono>useMarketshareParams()</Mono></Td>
              <Td><Mono>marketshareParamSchema</Mono></Td>
              <Td>tradeFlow</Td>
            </tr>
            <tr>
              <Td><Mono>useProductspaceParams()</Mono></Td>
              <Td><Mono>productspaceParamSchema</Mono></Td>
              <Td>rcaThreshold, colorBy</Td>
            </tr>
            <tr>
              <Td><Mono>useFeasibilityParams()</Mono></Td>
              <Td><Mono>feasibilityParamSchema</Mono></Td>
              <Td>sortBy</Td>
            </tr>
          </tbody>
        </Table>

        <SubSectionTitle>Provider Hierarchy</SubSectionTitle>
        <CodeBlock
          code={`// src/main.tsx
<HelmetProvider>
  <ThemeVars />              {/* CSS custom properties on :root */}
  <TutorialProvider>         {/* Product Space tutorial state */}
    <ApolloProvider>         {/* GraphQL cache layer */}
      <LocalizationProvider> {/* Fluent i18n (English) */}
        <RouterProvider />   {/* React Router v7 */}
      </LocalizationProvider>
    </ApolloProvider>
  </TutorialProvider>
</HelmetProvider>`}
        />
      </Section>

      {/* ── Data Flow ───────────────────────────────────────── */}
      <Section>
        <SectionTitle>Data Flow</SectionTitle>
        <Paragraph>
          Data flows unidirectionally from URL params through Apollo queries to
          chart renderers. Controls update the URL, which triggers a re-parse
          and re-render cycle.
        </Paragraph>

        <DataFlowDiagram />

        <SubSectionTitle>Connected Chart Pattern</SubSectionTitle>
        <Paragraph>
          Each visualization has a &quot;connected&quot; component in{" "}
          <Mono>src/visualization/connectedCharts/</Mono> that orchestrates
          the full pipeline:
        </Paragraph>
        <CodeBlock
          code={`// ConnectedTreemap.tsx — typical connected chart

// 1. Read typed params from URL
const [{ exporter, importer, year, view, productClass, ... }] = useTreemapParams();

// 2. Resolve metadata for lookups
const { locationsMetadata, productsMetadata } = useVisualizationMetadata();

// 3. Build GraphQL variables from params
const queryVariables = useMemo(() => ({ productClass, year, ... }), [...]);

// 4. Execute query (Apollo auto-caches)
const { data, loading } = useQuery(queryToUse, { variables: queryVariables });

// 5. Transform raw data for visualization
const result = useTreemapDataTransformation({ inputData: data, ... });

// 6. Build hierarchy and render
const hierarchy = useTreemapHierarchy({ result, chartWidth, chartHeight });

return (
  <VisualizationLayout totalValue={...} findInVizOptions={...}>
    <TreemapChart root={hierarchy.root} ... />
  </VisualizationLayout>
);`}
        />
      </Section>

      {/* ── Component Tree ──────────────────────────────────── */}
      <Section>
        <SectionTitle>Explore Page Component Tree</SectionTitle>
        <Paragraph>
          The <Mono>/explore</Mono> route renders <Mono>VisApp</Mono> which
          composes the control panel, visualization layout, and chart outlet.
        </Paragraph>

        <ComponentTreeDiagram />

        <BulletList>
          <li>
            <strong>VisApp</strong> — outer shell: preloads metadata, detects
            mobile, manages download context
          </li>
          <li>
            <strong>ControlsPanel</strong> — reads current <Mono>VizType</Mono>{" "}
            from URL path, delegates to a panel variant
          </li>
          <li>
            <strong>Each dropdown</strong> — wraps a base component from{" "}
            <Mono>@atlas-int/controls</Mono>, injects URL state via{" "}
            <Mono>useExploreParams()</Mono>
          </li>
          <li>
            <strong>Connected Chart</strong> — rendered via React Router{" "}
            <Mono>{"<Outlet />"}</Mono>, one per viz route
          </li>
        </BulletList>
      </Section>

      {/* ── Package Integration ─────────────────────────────── */}
      <Section>
        <SectionTitle>Package Integration Map</SectionTitle>
        <Paragraph>
          Each package has a specific role in the application. The app layer
          provides thin wrappers that inject URL state and analytics callbacks.
        </Paragraph>

        <Table>
          <thead>
            <tr>
              <Th>Package</Th>
              <Th>Role in App</Th>
              <Th>Integration Pattern</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td><Mono>@atlas-int/tokens</Mono></Td>
              <Td>Styling foundation</Td>
              <Td>
                Imported directly into styled components. <Mono>ThemeVars</Mono>{" "}
                injects CSS custom properties at <Mono>:root</Mono>.{" "}
                <Mono>colors</Mono>, <Mono>spacing</Mono>,{" "}
                <Mono>fontSize</Mono> used in ~126 files.
              </Td>
            </tr>
            <tr>
              <Td><Mono>@atlas-int/data</Mono></Td>
              <Td>Data layer & type system</Td>
              <Td>
                <strong>Types/enums</strong> used everywhere for type safety.{" "}
                <strong>Zod schemas</strong> validate URL params.{" "}
                <strong>GraphQL queries</strong> fetched via Apollo.{" "}
                <strong>Hooks</strong> (useDataAvailability,
                useClassificationConfig) resolve metadata.{" "}
                <strong>Utils</strong> (titleGeneration, locationUtils)
                transform data for display.
              </Td>
            </tr>
            <tr>
              <Td><Mono>@atlas-int/ui</Mono></Td>
              <Td>UI primitives</Td>
              <Td>
                Components used directly: <Mono>Button</Mono>,{" "}
                <Mono>Modal</Mono>, <Mono>Table</Mono>, <Mono>Flag</Mono>,{" "}
                <Mono>DataChip</Mono>, <Mono>Text</Mono>, etc. Hooks like{" "}
                <Mono>useWindowResize</Mono> provide responsive behavior.
              </Td>
            </tr>
            <tr>
              <Td><Mono>@atlas-int/controls</Mono></Td>
              <Td>Visualization controls</Td>
              <Td>
                App wraps each base control (LocationDropdown, ProductDropdown,
                YearDropdown, etc.) with a thin adapter in{" "}
                <Mono>src/components/controls/</Mono> that injects URL
                state via <Mono>useExploreParams()</Mono> and metadata from
                Apollo. The <Mono>onChange</Mono> handler calls{" "}
                <Mono>setQuery()</Mono> to update the URL.
              </Td>
            </tr>
            <tr>
              <Td><Mono>@atlas-int/viz</Mono></Td>
              <Td>Chart rendering</Td>
              <Td>
                Each chart type imported via subpath (<Mono>@atlas-int/viz/treemap</Mono>,
                etc.). Connected components in{" "}
                <Mono>src/visualization/connectedCharts/</Mono> orchestrate the
                query → transform → render pipeline. Shared components (legends,
                axes, tooltips) from <Mono>@atlas-int/viz/shared</Mono>.
              </Td>
            </tr>
          </tbody>
        </Table>

        <SubSectionTitle>Controls Wrapper Pattern</SubSectionTitle>
        <Paragraph>
          The app never uses package controls directly. Each control has a thin
          wrapper in <Mono>src/components/controls/</Mono> that:
        </Paragraph>
        <BulletList>
          <li>
            Reads current params via <Mono>useExploreParams()</Mono>
          </li>
          <li>
            Fetches metadata (locations, products, years) from Apollo via
            data hooks
          </li>
          <li>
            Transforms metadata into the shape the base control expects
          </li>
          <li>
            Passes a <Mono>setQuery()</Mono> callback as the{" "}
            <Mono>onChange</Mono> handler
          </li>
          <li>
            Applies visualization-specific constraints (e.g., Markets view
            requires one World location)
          </li>
        </BulletList>
        <CodeBlock
          code={`// src/components/controls/locationDropdown/LocationDropdown.tsx
// Typical wrapper pattern

const [{ exporter }, setQuery] = useExploreParams();
const { countries, regions, subregions } = useLocationsMetadata();

// Transform to flat list for the base component
const flatLocations = useMemo(() => {
  const items: LocationItem[] = [];
  items.push({ id: worldGroupDatum.groupId, isSticky: true });
  regions.forEach(region => { /* nest subregions + countries */ });
  return items;
}, [countries, regions, subregions]);

// URL mutation on selection
const handleSelect = (id: string) => setQuery({ exporter: id });

return <LocationDropdownBase products={flatLocations} value={exporter} onChange={handleSelect} />;`}
        />
      </Section>

      {/* ── Apollo Integration ──────────────────────────────── */}
      <Section>
        <SectionTitle>Apollo Client & GraphQL</SectionTitle>
        <Paragraph>
          The app is read-only — no mutations. Apollo Client is created via{" "}
          <Mono>createAtlasClient()</Mono> from <Mono>@atlas-int/data</Mono>{" "}
          and configured as a singleton in{" "}
          <Mono>src/graphql/apolloClient.ts</Mono>.
        </Paragraph>

        <SubSectionTitle>Query Categories</SubSectionTitle>
        <Table>
          <thead>
            <tr>
              <Th>Category</Th>
              <Th>Queries</Th>
              <Th>When Fetched</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>Metadata</Td>
              <Td>
                <Mono>getLocationsMetadata</Mono>,{" "}
                <Mono>getHs92ProductsMetadata</Mono>,{" "}
                <Mono>getCountryYearMetadata</Mono>, etc.
              </Td>
              <Td>
                Preloaded on VisApp mount, cached for session
              </Td>
            </tr>
            <tr>
              <Td>Availability</Td>
              <Td>
                <Mono>getDataAvailability</Mono>, <Mono>getDataFlags</Mono>
              </Td>
              <Td>
                Fetched once, drives year range & classification filtering
              </Td>
            </tr>
            <tr>
              <Td>Trade data</Td>
              <Td>
                <Mono>CPY_QUERY</Mono>, <Mono>CCPY_QUERY</Mono>,{" "}
                <Mono>GGPY_QUERY</Mono>, <Mono>CCY_QUERY</Mono>, etc.
              </Td>
              <Td>
                On-demand when URL params change; Apollo deduplicates &amp;
                caches by query + variables
              </Td>
            </tr>
            <tr>
              <Td>Utility</Td>
              <Td>
                <Mono>getBanner</Mono>, <Mono>getDownloadsData</Mono>,{" "}
                <Mono>getCpyGrowth</Mono>
              </Td>
              <Td>
                Page-specific (banner on all pages, downloads on data page)
              </Td>
            </tr>
          </tbody>
        </Table>

        <SubSectionTitle>Stale Data Strategy</SubSectionTitle>
        <Paragraph>
          Connected charts use <Mono>data || previousData</Mono> so the
          previous visualization stays visible while the next dataset loads.
          This prevents flash-of-empty during parameter changes.
        </Paragraph>
        <CodeBlock
          code={`const { data, previousData, loading } = useQuery(queryToUse, {
  variables: queryVariables,
  skip: queryIsInvalid,
});

// Keep showing previous chart while next one loads
const inputData = data ?? previousData;`}
        />
      </Section>

      {/* ── Code Splitting ──────────────────────────────────── */}
      <Section>
        <SectionTitle>Code Splitting</SectionTitle>
        <Paragraph>
          Large sections are lazy-loaded to reduce the initial bundle size.
        </Paragraph>
        <Table>
          <thead>
            <tr>
              <Th>Section</Th>
              <Th>Strategy</Th>
              <Th>Reason</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>Country Profiles (<Mono>/countries</Mono>)</Td>
              <Td><Mono>React.lazy()</Mono></Td>
              <Td>Large module with own Redux store, GreenSock animations</Td>
            </tr>
            <tr>
              <Td>Rankings (<Mono>/rankings</Mono>)</Td>
              <Td><Mono>React.lazy()</Mono></Td>
              <Td>Standalone layout, no shared state with explore</Td>
            </tr>
            <tr>
              <Td>Viz chart subpaths</Td>
              <Td>Vite entry points per chart</Td>
              <Td>
                Each chart type (<Mono>@atlas-int/viz/treemap</Mono>, etc.)
                is a separate build entry, enabling tree-shaking
              </Td>
            </tr>
          </tbody>
        </Table>
      </Section>

      {/* ── URL Migration ───────────────────────────────────── */}
      <Section>
        <SectionTitle>URL Migration</SectionTitle>
        <Paragraph>
          The <Mono>URLMigrationWrapper</Mono> component runs once on initial
          page load to convert legacy URL formats (missing{" "}
          <Mono>tradeDirection</Mono> param) to the current format. This
          preserves backward compatibility with bookmarked and shared links.
        </Paragraph>
        <CodeBlock
          code={`// src/routing/urlMigration.ts
const { needsMigration, migratedParams } = migrateOldUrlToNewFormat(params);

if (needsMigration) {
  navigate({ pathname, search: migratedParams.toString() }, { replace: true });
}`}
        />
      </Section>
    </div>
  );
};

export default ArchitecturePage;
